import { useTranslation } from "react-i18next";
import { Search, X } from "lucide-react";
import { Input } from "@/shared/components/ui/input";
import { Button } from "@/shared/components/ui/button";
import type { Role } from "@/shared/types/role";
import { ROLE_FILTER_ORDER, USER_STATUS_FILTERS } from "./constants";
import type { UserStatusFilter } from "./types";

interface UsersFiltersProps {
  readonly query: string;
  readonly role: Role | null;
  readonly status: UserStatusFilter;
  readonly onQueryChange: (value: string) => void;
  readonly onRoleChange: (value: Role | null) => void;
  readonly onStatusChange: (value: UserStatusFilter) => void;
  readonly onReset: () => void;
}

export function UsersFilters({
  query,
  role,
  status,
  onQueryChange,
  onRoleChange,
  onStatusChange,
  onReset,
}: UsersFiltersProps) {
  const { t } = useTranslation();
  const hasFilters = query.length > 0 || role !== null || status !== "all";

  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="min-w-[260px] flex-1 space-y-1.5">
        <label htmlFor="users-search" className="font-sans text-sm font-medium text-foreground">
          {t("users.filters.search")}
        </label>
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            id="users-search"
            data-testid="users-search-input"
            type="search"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder={t("users.filters.searchPlaceholder")}
            className="pl-9"
            autoComplete="off"
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <label
          htmlFor="users-role-filter"
          className="font-sans text-sm font-medium text-foreground"
        >
          {t("users.filters.role")}
        </label>
        <select
          id="users-role-filter"
          data-testid="filter-role"
          value={role ?? ""}
          onChange={(e) => {
            const value = e.target.value;
            onRoleChange(value === "" ? null : (value as Role));
          }}
          className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <option value="">{t("users.filters.roleAll")}</option>
          {ROLE_FILTER_ORDER.map((r) => (
            <option key={r} value={r}>
              {t(`users.roleLabel.${r}`)}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-1.5">
        <label
          htmlFor="users-status-filter"
          className="font-sans text-sm font-medium text-foreground"
        >
          {t("users.filters.status")}
        </label>
        <select
          id="users-status-filter"
          data-testid="filter-status"
          value={status}
          onChange={(e) => onStatusChange(e.target.value as UserStatusFilter)}
          className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          {USER_STATUS_FILTERS.map((s) => (
            <option key={s} value={s}>
              {t(`users.filters.statusOption.${s}`)}
            </option>
          ))}
        </select>
      </div>

      {hasFilters && (
        <Button
          data-testid="users-filters-reset"
          variant="ghost"
          onClick={onReset}
          className="h-10"
        >
          <X className="mr-1 h-4 w-4" />
          {t("users.filters.reset")}
        </Button>
      )}
    </div>
  );
}
